import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { positionRank, isExecutiveLevel, SUPERVISOR_MIN_RANK } from '../lib/positionRank';
import './AdminEmployeeMaster.css';

const STATUS_CHOICES = [
  { value: 'ACTIVATE', label: '在籍' },
  { value: 'RESIGNED', label: '退職' },
];

// 値は DB・django-admin と同じ「男 / 女」。表示ラベルは「男性 / 女性」。
const SEX_CHOICES = [
  { value: '', label: '未設定' },
  { value: '男', label: '男性' },
  { value: '女', label: '女性' },
  { value: 'その他', label: 'その他' },
];

// 旧データ（男性/女性）も選択肢（男/女）に合わせて正規化して表示する
const normSex = (v) => {
  const s = String(v || '').trim();
  if (s === '男性') return '男';
  if (s === '女性') return '女';
  return s;
};

const EMPTY_EMPLOYEE = {
  employeeId: '',
  memberId: '',
  name: '',
  nameFuri: '',
  nameRomaji: '',
  sex: '',
  birthDate: '',
  departmentName: '',
  position: '',
  employee: '',
  employeeType: '',
  work_type: 'NORMAL',
  project: null,
  status: 'ACTIVATE',
  startDate: '',
  endDate: '',
  prePosition: '',
  zipcode: '',
  address: '',
  tel: '',
  email: '',
  trainFare: 0,
  fuyoucount: 0,
  issueDate: '',
  annualLeaveEntitled: 10,
  annualLeaveUsed: 0,
  is_deleted: false,
  password: '',
};

// 表のセルで直接編集できる項目（勤務地/上司/写真などの複雑な項目はモーダルのみ）
const INLINE_FIELDS = ['memberId', 'name', 'nameFuri', 'departmentName', 'position', 'status', 'startDate', 'email', 'tel'];

const INLINE_INPUT_STYLE = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '4px 6px',
  border: '1px solid #d9d9d9',
  borderRadius: 4,
  fontSize: '0.85rem',
  background: '#fff',
};

// 値が全部見えるよう、列ごとの最小幅(px)。表は横スクロールで対応。
const INLINE_FIELD_WIDTH = {
  memberId: 120,
  name: 180,
  nameFuri: 180,
  departmentName: 200,
  position: 150,
  status: 100,
  startDate: 130,
  email: 240,
  tel: 150,
  workplace: 170,
  supervisors: 220,
};

// 列ごとの最小幅を付けたインライン入力スタイルを返す
const inlineStyle = (field) => ({ ...INLINE_INPUT_STYLE, minWidth: INLINE_FIELD_WIDTH[field] || 100 });

// Excelの日付セル(Dateオブジェクト)を YYYY-MM-DD 文字列に整形する。
// （Excelは日付をシリアル値=46174等で保持するため、そのままだと数値が表示される）
const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const normalizeExcelValue = (v) => (v instanceof Date ? toYmd(v) : v);

// 固定列（コード・氏名）の入力欄は、列の固定幅に収まるよう最小幅を外す
const STICKY_INPUT_STYLE = { ...INLINE_INPUT_STYLE, minWidth: 0 };

const FIELD_LABELS = {
  employeeId: '社員コード',
  memberId: 'コード',
  name: '氏名',
  nameFuri: 'フリガナ',
  nameRomaji: '氏名（ローマ字）',
  sex: '性別',
  birthDate: '生年月日',
  departmentName: '所属部門名',
  position: '役職名',
  employee: '従業員区分',
  employeeType: '雇用区分',
  status: '就業状況',
  startDate: '入社年月日',
  endDate: '退社年月日',
  prePosition: '前職あり',
  zipcode: '郵便番号',
  address: '住所',
  tel: '電話番号',
  email: 'メールアドレス',
  trainFare: '通勤費',
  fuyoucount: '扶養人数',
  issueDate: '発行日',
  password: 'パスワード（初期）',
};

// Excelの列名がシステム項目名と完全一致しなくても自動対応づけできるよう、よくある別名を登録。
// （取込時の自動マッピングで使用。空白/大文字小文字/全角空白は無視して比較）
const FIELD_SYNONYMS = {
  employeeId: ['社員コード', '社員ID', '社員番号', '従業員コード', '従業員番号'],
  memberId: ['コード', 'ログインID', '会員コード', 'メンバーID'],
  name: ['氏名', '名前', '名称', '社員名', '従業員名', 'name'],
  nameFuri: ['フリガナ', 'ふりがな', 'カナ', '氏名カナ', '氏名フリガナ'],
  nameRomaji: ['ローマ字', '氏名ローマ字', 'ローマ字氏名', '名前ローマ字'],
  sex: ['性別'],
  birthDate: ['生年月日', '誕生日'],
  departmentName: ['所属部門名', '所属部門', '部門名', '部門', '部署名', '部署', '所属', '所属部署'],
  position: ['役職名', '役職', '職位'],
  employee: ['従業員区分', '社員区分'],
  employeeType: ['雇用区分', '雇用形態'],
  status: ['就業状況', '在籍状況', 'ステータス', '状態'],
  startDate: ['入社年月日', '入社日', '入社年月', '採用日'],
  endDate: ['退社年月日', '退社日', '退職日'],
  prePosition: ['前職あり', '前職'],
  zipcode: ['郵便番号', '〒', '郵便'],
  address: ['住所', '現住所'],
  tel: ['電話番号', '電話', 'TEL', '携帯電話', '携帯'],
  email: ['メールアドレス', 'メール', 'Email', 'E-mail', 'メアド'],
  trainFare: ['通勤費', '交通費', '通勤手当'],
  fuyoucount: ['扶養人数', '扶養家族', '扶養'],
  issueDate: ['発行日', '発行年月日'],
  password: ['パスワード', '初期パスワード', 'パスコード', 'PW', 'password'],
};

// 列名比較用の正規化（前後空白・全角/半角空白除去、小文字化）
const normHeader = (s) => String(s || '').trim().toLowerCase().replace(/[\s　]+/g, '');

export default function AdminEmployeeMaster() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [workplaces, setWorkplaces] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);


  const [excelHeaders, setExcelHeaders] = useState([]); 
  const [excelRows, setExcelRows] = useState([]);      
  const [columnMapping, setColumnMapping] = useState({}); 
  const [showMappingModal, setShowMappingModal] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [formData, setFormData] = useState(EMPTY_EMPLOYEE);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // 表のセル直接編集用：未保存の行ID と 行保存中の行ID
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [savingRowId, setSavingRowId] = useState(null);
  // 上司セルを編集中の行ID（普段は値だけ表示、クリックで編集に切替）
  const [editingSupId, setEditingSupId] = useState(null);

  const [photoModal, setPhotoModal] = useState({ open: false, employee: null });
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  const editPhotoInputRef = useRef(null);
  const importInputRef = useRef(null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (searchKeyword) params.q = searchKeyword;
      if (includeDeleted) params.include_deleted = 'true';
      const res = await api.get('/api/admin/employee-master/', { params });
      const data = res.data?.results ?? res.data ?? [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch {
      setError('社員データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [searchKeyword, includeDeleted]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    api.get('/api/admin/workplaces/')
      .then((res) => setWorkplaces(Array.isArray(res.data) ? res.data : []))
      .catch(() => setWorkplaces([]));
    // 部署ツリー（上司候補を「同じ課＋上位部の部長」に絞るための親子関係に使用）
    api.get('/api/departments/')
      .then((res) => setDepartments(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDepartments([]));
    // プロジェクト一覧（社員へのプロジェクト割当用）
    api.get('/api/admin/projects/')
      .then((res) => setProjects(Array.isArray(res.data) ? res.data : (res.data?.results ?? [])))
      .catch(() => setProjects([]));
  }, []);

  // 部署名 → 上位部署名 のマップ（部署ツリーを平坦化）
  const parentByDeptName = useMemo(() => {
    const map = {};
    const walk = (nodes, parentName) => {
      (nodes || []).forEach((n) => {
        if (n.departmentName && parentName) map[n.departmentName] = parentName;
        walk(n.children, n.departmentName);
      });
    };
    walk(departments, null);
    return map;
  }, [departments]);

  // 指定部署の上位部署をルートまで遡って集合で返す（課→部→本部→…）。
  // 1階層しか見ないと、課長の上司に部長・本部長を選べない。
  const ancestorDeptsOf = useCallback((deptName) => {
    const acc = new Set();
    let cur = parentByDeptName[deptName] || null;
    while (cur && !acc.has(cur)) {
      acc.add(cur);
      cur = parentByDeptName[cur] || null;
    }
    return acc;
  }, [parentByDeptName]);

  // 上司の候補：
  //   1. 経営層（社長・取締役など）は部署に関係なく常に候補にする。
  //      社長は部署未所属のことがあり、部署ツリーを辿るだけでは絶対に出てこないため。
  //   2. 同じ課/部の社員。
  //   3. 上位部署（何階層でも遡る）にいる役職者（リーダー相当以上）。
  // 上位ほど先に並ぶよう役職ランクの降順で返す。
  const supervisorCandidates = useCallback((emp) => {
    const self = emp || {};
    const dept = (self.departmentName || '').trim();
    const ancestors = dept ? ancestorDeptsOf(dept) : new Set();

    return employees
      .filter((x) => {
        if (x.id === self.id) return false;
        const xPos = String(x.position || '');
        if (isExecutiveLevel(xPos)) return true;
        if (!dept) return true; // 自分の部署が未設定なら全社員から選ぶ
        const xDept = (x.departmentName || '').trim();
        if (xDept === dept) return true;
        return ancestors.has(xDept) && positionRank(xPos) >= SUPERVISOR_MIN_RANK;
      })
      .sort((a, b) => {
        const diff = positionRank(b.position) - positionRank(a.position);
        if (diff !== 0) return diff;
        return String(a.memberId || a.employeeId || '').localeCompare(String(b.memberId || b.employeeId || ''));
      });
  }, [employees, ancestorDeptsOf]);

  // 候補セレクトの表示名。役職と部署が分からないと社長かどうか判断できないため併記する。
  const supervisorOptionLabel = (x) => {
    const code = x.memberId || x.employeeId || '';
    const meta = [x.position, x.departmentName].filter(Boolean).join('／');
    return `${code} ${x.name || ''}${meta ? `（${meta}）` : ''}`;
  };

  const openAdd = () => {
    setFormData({ ...EMPTY_EMPLOYEE });
    setModalMode('add');
    setShowModal(true);
  };

  const openEdit = (emp) => {
    setFormData({ ...EMPTY_EMPLOYEE, ...emp });
    setModalMode('edit');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(EMPTY_EMPLOYEE);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSupervisorsChange = (e) => {
    const ids = Array.from(e.target.selectedOptions).map((o) => o.value);
    setFormData((prev) => ({ ...prev, supervisors: ids }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.employeeId?.trim()) {
      alert('社員コードは必須です。');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        workplace: formData.workplace || null,
        project: formData.project || null,
        work_type: formData.work_type || 'NORMAL',
      };
      // パスワードは空欄なら送らない（既存パスワードを変更しないため）
      if (!payload.password || !payload.password.trim()) {
        delete payload.password;
      }
      if (modalMode === 'add') {
        await api.post('/api/admin/employee-master/', payload);
        alert(payload.password
          ? '社員を追加し、ログインアカウントを作成しました。'
          : '社員を追加しました。');
      } else {
        await api.patch(`/api/admin/employee-master/${formData.id}/`, payload);
        alert('社員情報を更新しました。');
      }
      closeModal();
      fetchEmployees();
    } catch (err) {
      const msg = err.response?.data
        ? JSON.stringify(err.response.data)
        : '保存に失敗しました。';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await api.get('/api/admin/employee-master/export-csv/', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('CSVの書き出しに失敗しました。');
    }
  };

  // エクセルファイルを読み込んで、列のマッピングを行う処理
  const handleExcelUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; 
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        // cellDates: 日付セルをシリアル値ではなくDateとして読み込む
        const wb = XLSX.read(bstr, { type: 'binary', cellDates: true });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        
        // シートからJSON形式でデータを取得（空セルは空文字列にする）
        const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (data.length === 0) {
          alert("File Excel にはデータがありません。");
          return;
        }

        // 実際の列ヘッダーのリストを取得
        const headers = Object.keys(data[0]);
        setExcelHeaders(headers);
        setExcelRows(data);

        // 自動的にマッピングを推測（項目名・ラベル・別名のいずれかと一致すれば対応づけ）
        const initialMapping = {};
        Object.keys(FIELD_LABELS).forEach(dbField => {
          // この項目に対応づく候補名（フィールド名・表示ラベル・別名一覧）を正規化
          const candidates = [dbField, FIELD_LABELS[dbField], ...(FIELD_SYNONYMS[dbField] || [])]
            .map(normHeader);
          const match = headers.find(h => candidates.includes(normHeader(h)));
          initialMapping[dbField] = match || "";
        });
        
        setColumnMapping(initialMapping);
        setShowMappingModal(true);
      } catch (err) {
        alert("Excelファイルの読み込みに失敗しました。ファイル形式を確認してください。");
      }
    };
    reader.readAsBinaryString(file);
  };

  // バックエンドに対して、Excelからのデータを処理するAPIを呼び出す
  const handleProcessImport = async () => {
    // 社員コード（employeeId）カラムがマッピングされていない場合はエラー
    if (!columnMapping.employeeId) {
      alert("社員コード（employeeId）カラムがマッピングされていません。社員コードは必須項目です。");
      return;
    }

    setSaving(true);
    try {
      // Excelの行データを、APIが期待する形式に変換する
      const stdData = excelRows.map(row => {
        const item = {};
        Object.keys(columnMapping).forEach(dbField => {
          const excelHeaderName = columnMapping[dbField];
          if (excelHeaderName) {
            // 日付セルは YYYY-MM-DD に整形（46174 のようなシリアル値表示を防ぐ）
            item[dbField] = normalizeExcelValue(row[excelHeaderName]);
          }
        });
        return item;
      });

      // APIにデータを送信して処理を実行
      const res = await api.post('/api/admin/employee-master/import-excel-json/', {
        data: stdData
      });

      const { created = 0, updated = 0, errors = [] } = res.data || {};
      let msg = `取込完了 ：新規 ${created} 件、更新 ${updated} 件。`;
      if (errors.length) msg += `\n警告 ${errors.length} 件:\n` + errors.slice(0, 20).join('\n');
      
      alert(msg);
      setShowMappingModal(false);
      fetchEmployees();
    } catch (err) {
      alert(err.response?.data?.detail || 'Importに失敗しました。ファイルの内容を確認してください。');
    } finally {
      setSaving(false);
    }
  };

  // 表のセルを直接編集したとき：その行をローカル更新し「未保存」に印を付ける
  const handleInlineChange = (id, field, value) => {
    setEmployees((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // 行の「保存」ボタン：編集した項目だけをPATCHしてDBを更新
  const handleRowSave = async (emp) => {
    setSavingRowId(emp.id);
    try {
      const payload = {};
      INLINE_FIELDS.forEach((f) => { payload[f] = emp[f] ?? ''; });
      // 勤務地(FK)・上司(複数)も保存。空の勤務地は未割当(null)に正規化。
      payload.workplace = emp.workplace || null;
      payload.supervisors = Array.isArray(emp.supervisors) ? emp.supervisors : [];
      // シフト勤務タイプ・プロジェクト(FK)も保存
      payload.work_type = emp.work_type || 'NORMAL';
      payload.project = emp.project || null;
      const res = await api.patch(`/api/admin/employee-master/${emp.id}/`, payload);
      // サーバの正規化済みデータでその行を更新
      setEmployees((prev) => prev.map((e) => (e.id === emp.id ? { ...e, ...res.data } : e)));
      setDirtyIds((prev) => {
        const next = new Set(prev);
        next.delete(emp.id);
        return next;
      });
    } catch (err) {
      const msg = err.response?.data ? JSON.stringify(err.response.data) : '保存に失敗しました。';
      alert(msg);
    } finally {
      setSavingRowId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/admin/employee-master/${deleteTarget.id}/`);
      alert(`${deleteTarget.name || deleteTarget.employeeId} を削除（退職フラグ設定）しました。`);
      setDeleteTarget(null);
      fetchEmployees();
    } catch {
      alert('削除に失敗しました。');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEmployees();
  };

  const openPhotoModal = (emp) => {
    setPhotoModal({ open: true, employee: emp });
  };

  const closePhotoModal = () => {
    setPhotoModal({ open: false, employee: null });
    setPhotoDragOver(false);
  };

  const handlePhotoUpload = async (file, empId, onSuccess) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください。');
      return;
    }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const res = await api.post(
        `/api/admin/employee-master/${empId}/upload_photo/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (onSuccess) onSuccess(res.data.photo_url);
      fetchEmployees();
    } catch {
      alert('写真のアップロードに失敗しました。');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!photoModal.employee) return;
    if (!window.confirm('写真を削除しますか？')) return;
    try {
      await api.post(`/api/admin/employee-master/${photoModal.employee.id}/delete_photo/`);
      setPhotoModal(prev => ({
        ...prev,
        employee: { ...prev.employee, photo_url: null },
      }));
      fetchEmployees();
    } catch {
      alert('写真の削除に失敗しました。');
    }
  };

  return (
    <div className="admin-layout emp-master-page">
      <div className="emp-master-header">
        <h2 className="emp-master-title">社員マスター</h2>
        <div className="emp-master-actions">
          <button type="button" className="btn-emp-back" onClick={() => navigate('/admin/employee-card-setting')}>
            戻る
          </button>
          
          {/* ExcelインポートとCSVエクスポートのボタン */}
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx, .xls"
            style={{ display: 'none' }}
            onChange={handleExcelUpload}
          />
          <button type="button" className="btn-emp-add" style={{ marginRight: 8, backgroundColor: '#2e7d32' }} onClick={() => importInputRef.current?.click()}>
            Excelからインポート 
          </button>
          
          <button type="button" className="btn-emp-add" onClick={handleExportCsv}>
            CSVエクスポート
          </button>
        </div>
      </div>

      {/* 検索 */}
      <form className="emp-search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          className="emp-search-input"
          placeholder="社員コード / コード / 氏名 / 部門名で検索"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
        />
        <label className="emp-deleted-check">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          削除済みも含む
        </label>
        <button type="submit" className="btn-emp-search">検索</button>
      </form>

      {loading && <p className="emp-status-msg">読み込み中...</p>}
      {error && <p className="emp-error-msg">{error}</p>}

      {!loading && !error && (
        <div className="emp-table-wrapper">
          <table className="emp-table">
            <thead>
              <tr>
                <th>社員コード</th>
                <th>コード</th>
                <th>氏名</th>
                <th>フリガナ</th>
                <th>性別</th>
                <th>部門名</th>
                <th>役職</th>
                <th>就業状況</th>
                <th>入社年月日</th>
                <th>メール</th>
                <th>電話番号</th>
                <th>勤務地</th>
                <th>シフト勤務タイプ</th>
                <th>プロジェクト</th>
                <th>上司</th>
                <th>写真</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td colSpan={17} className="emp-no-data">
                    該当する社員が見つかりません。
                  </td>
                </tr>
              ) : (
                employees.map((emp) => {
                  const isDirty = dirtyIds.has(emp.id);
                  return (
                  <tr key={emp.id} className={`${emp.is_deleted ? 'emp-row-deleted' : ''}${isDirty ? ' emp-row-dirty' : ''}`}>
                    {/* 社員コードはログインID兼キーのため直接編集不可（修正ボタンから） */}
                    <td>{emp.employeeId}</td>
                    <td>
                      <input style={STICKY_INPUT_STYLE} value={emp.memberId || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'memberId', e.target.value)} />
                    </td>
                    <td>
                      <input style={STICKY_INPUT_STYLE} value={emp.name || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'name', e.target.value)} />
                    </td>
                    <td>
                      <input style={inlineStyle('nameFuri')} value={emp.nameFuri || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'nameFuri', e.target.value)} />
                    </td>
                    <td>
                      <select style={inlineStyle('sex')} value={normSex(emp.sex)}
                        onChange={(e) => handleInlineChange(emp.id, 'sex', e.target.value)}>
                        {SEX_CHOICES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input style={inlineStyle('departmentName')} value={emp.departmentName || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'departmentName', e.target.value)} />
                    </td>
                    <td>
                      <input style={inlineStyle('position')} value={emp.position || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'position', e.target.value)} />
                    </td>
                    <td>
                      <select style={inlineStyle('status')} value={emp.status || 'ACTIVATE'}
                        onChange={(e) => handleInlineChange(emp.id, 'status', e.target.value)}>
                        {STATUS_CHOICES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input style={inlineStyle('startDate')} value={emp.startDate || ''} placeholder="YYYY-MM-DD"
                        onChange={(e) => handleInlineChange(emp.id, 'startDate', e.target.value)} />
                    </td>
                    <td>
                      <input style={inlineStyle('email')} value={emp.email || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'email', e.target.value)} />
                    </td>
                    <td>
                      <input style={inlineStyle('tel')} value={emp.tel || ''}
                        onChange={(e) => handleInlineChange(emp.id, 'tel', e.target.value)} />
                    </td>
                    {/* 勤務地(FK)：選択で直接編集 */}
                    <td>
                      <select style={inlineStyle('workplace')} value={emp.workplace == null ? '' : String(emp.workplace)}
                        onChange={(e) => handleInlineChange(emp.id, 'workplace', e.target.value)}>
                        <option value="">（未割当）</option>
                        {workplaces.map((w) => (
                          <option key={w.id} value={String(w.id)}>{w.workplace_name || w.id}</option>
                        ))}
                      </select>
                    </td>
                    {/* シフト勤務タイプ：選択で直接編集 */}
                    <td>
                      <select style={inlineStyle('work_type')} value={emp.work_type || 'NORMAL'}
                        onChange={(e) => handleInlineChange(emp.id, 'work_type', e.target.value)}>
                        <option value="NORMAL">通常</option>
                        <option value="SHIFT">シフト</option>
                      </select>
                    </td>
                    {/* プロジェクト(FK)：選択で直接編集 */}
                    <td>
                      <select style={inlineStyle('project')} value={emp.project == null ? '' : String(emp.project)}
                        onChange={(e) => handleInlineChange(emp.id, 'project', e.target.value)}>
                        <option value="">（未割当）</option>
                        {projects.map((p) => (
                          <option key={p.id} value={String(p.id)}>{p.project_name || p.id}</option>
                        ))}
                      </select>
                    </td>
                    {/* 上司(複数)：普段は値（S006,S076 / 未設定）だけ表示。枠をクリックで複数選択の編集に切替 */}
                    <td>
                      {editingSupId === emp.id ? (
                        <select multiple autoFocus
                          style={{ ...inlineStyle('supervisors'), minHeight: 64 }}
                          value={(emp.supervisors || []).map(String)}
                          onChange={(e) => handleInlineChange(emp.id, 'supervisors', Array.from(e.target.selectedOptions).map((o) => o.value))}
                          onBlur={() => setEditingSupId(null)}>
                          {supervisorCandidates(emp).map((x) => (
                            <option key={x.id} value={String(x.id)}>
                              {supervisorOptionLabel(x)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div
                          onClick={() => setEditingSupId(emp.id)}
                          title="クリックして上司を編集"
                          style={{
                            ...inlineStyle('supervisors'),
                            cursor: 'pointer',
                            minHeight: 28,
                            display: 'flex',
                            alignItems: 'center',
                            color: (emp.supervisors || []).length ? '#333' : '#999',
                          }}
                        >
                          {(emp.supervisors || []).length
                            ? (emp.supervisors || []).map((id) => {
                                const s = employees.find((e) => String(e.id) === String(id));
                                return s ? (s.memberId || s.employeeId || '') : id;
                              }).join(', ')
                            : '未設定'}
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`btn-emp-photo${emp.photo_url ? ' has-photo' : ''}`}
                        onClick={() => openPhotoModal(emp)}
                      >
                        {emp.photo_url ? '写真あり' : '写真なし'}
                      </button>
                    </td>
                    <td>{emp.is_deleted ? '削除済' : '有効'}</td>
                    <td className="emp-row-btns">
                      <button
                        type="button"
                        className="btn-emp-save"
                        onClick={() => handleRowSave(emp)}
                        disabled={!isDirty || savingRowId === emp.id}
                        title="表で編集した内容を保存します"
                      >
                        {savingRowId === emp.id ? '保存中...' : '保存'}
                      </button>
                      <button
                        type="button"
                        className="btn-emp-edit"
                        onClick={() => openEdit(emp)}
                      >
                        修正
                      </button>
                      {!emp.is_deleted && (
                        <button
                          type="button"
                          className="btn-emp-delete"
                          onClick={() => setDeleteTarget(emp)}
                        >
                          削除
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Excel列のマッピングモーダル */}
      {showMappingModal && (
        <div className="emp-modal-overlay">
          <div className="emp-modal" style={{ maxWidth: 600 }}>
            <h3 className="emp-modal-title">Excel列のマッピング </h3>
            <p style={{ fontSize: '0.9rem', marginBottom: 15, color: '#666' }}>
                以下の表で、左側の「システムのフィールド」と右側の「Excelの列」を対応させてください。<br />
            </p>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '5px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                    <th style={{ padding: 8 }}>システム (Database)</th>
                    <th style={{ padding: 8 }}>Excelファイルの列</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(FIELD_LABELS).map((dbField) => (
                    <tr key={dbField} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 4px', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {FIELD_LABELS[dbField]}
                      </td>
                      <td style={{ padding: '8px 4px' }}>
                        <select
                          value={columnMapping[dbField] || ''}
                          onChange={(e) => setColumnMapping(prev => ({ ...prev, [dbField]: e.target.value }))}
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="">-- 情報なし --</option>
                          {excelHeaders.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="emp-form-footer" style={{ marginTop: 20 }}>
              <button type="button" className="btn-emp-cancel" onClick={() => setShowMappingModal(false)}>
                キャンセル
              </button>
              <button type="button" className="btn-emp-save" onClick={handleProcessImport} disabled={saving}>
                {saving ? '処理中...' : 'インポート実行'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 追加・編集モーダル */}
      {showModal && (
        <div className="emp-modal-overlay" onClick={closeModal}>
          <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="emp-modal-title">
              {modalMode === 'add' ? '社員を追加' : '社員情報を修正'}
            </h3>
            <form onSubmit={handleSave} className="emp-form">
              <div className="emp-form-grid">
                {/* Section 1: 基本情報 */}
                <div className="emp-form-section">
                  <h4 className="emp-form-section-title">基本情報</h4>
                  <label className="emp-form-label">
                    社員コード <span className="required">*</span>
                    <input
                      type="text"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleChange}
                      required
                      className="emp-form-input"
                      disabled={modalMode === 'edit'}
                    />
                  </label>
                  <label className="emp-form-label">
                    コード（memberId）
                    <input
                      type="text"
                      name="memberId"
                      value={formData.memberId || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    ログインパスワード
                    <input
                      type="password"
                      name="password"
                      value={formData.password || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                      autoComplete="new-password"
                      placeholder={modalMode === 'add' ? 'ログイン用パスワード（任意）' : '変更する場合のみ入力'}
                    />
                    <small style={{ color: '#888', fontSize: '0.78rem' }}>
                      ログインID＝社員コード。入力すると社員用アカウントを作成/更新します。空欄なら変更しません。
                    </small>
                  </label>
                  <label className="emp-form-label">
                    氏名
                    <input
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    フリガナ
                    <input
                      type="text"
                      name="nameFuri"
                      value={formData.nameFuri || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    氏名（ローマ字）
                    <input
                      type="text"
                      name="nameRomaji"
                      value={formData.nameRomaji || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    性別
                    <select name="sex" value={normSex(formData.sex)} onChange={handleChange} className="emp-form-select">
                      {SEX_CHOICES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="emp-form-label">
                    生年月日
                    <input
                      type="text"
                      name="birthDate"
                      value={formData.birthDate || ''}
                      onChange={handleChange}
                      placeholder="YYYY-MM-DD"
                      className="emp-form-input"
                    />
                  </label>
                </div>

                {/* Section 2: 所属・役職 */}
                <div className="emp-form-section">
                  <h4 className="emp-form-section-title">所属・役職</h4>
                  <label className="emp-form-label">
                    所属部門名
                    <input
                      type="text"
                      name="departmentName"
                      value={formData.departmentName || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    勤務地
                    <select
                      name="workplace"
                      value={formData.workplace || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    >
                      <option value="">（未割当）</option>
                      {workplaces.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.workplace_name || w.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="emp-form-label">
                    シフト勤務タイプ
                    <select
                      name="work_type"
                      value={formData.work_type || 'NORMAL'}
                      onChange={handleChange}
                      className="emp-form-input"
                    >
                      <option value="NORMAL">通常</option>
                      <option value="SHIFT">シフト</option>
                    </select>
                  </label>
                  <label className="emp-form-label">
                    プロジェクト
                    <select
                      name="project"
                      value={formData.project || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    >
                      <option value="">（未割当）</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.project_name || p.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="emp-form-label">
                    上司（複数選択可・Ctrl/⌘で複数）
                    <select
                      multiple
                      value={(formData.supervisors || []).map(String)}
                      onChange={handleSupervisorsChange}
                      className="emp-form-input"
                      style={{ minHeight: 110 }}
                    >
                      {supervisorCandidates(formData)
                        .map((e) => (
                          <option key={e.id} value={e.id}>
                            {supervisorOptionLabel(e)}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="emp-form-label">
                    役職名
                    <input
                      type="text"
                      name="position"
                      value={formData.position || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    従業員区分
                    <input
                      type="text"
                      name="employee"
                      value={formData.employee || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    雇用区分
                    <input
                      type="text"
                      name="employeeType"
                      value={formData.employeeType || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    就業状況
                    <select name="status" value={formData.status || 'ACTIVATE'} onChange={handleChange} className="emp-form-select">
                      {STATUS_CHOICES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="emp-form-label">
                    入社年月日
                    <input
                      type="text"
                      name="startDate"
                      value={formData.startDate || ''}
                      onChange={handleChange}
                      placeholder="YYYY-MM-DD"
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    退社年月日
                    <input
                      type="text"
                      name="endDate"
                      value={formData.endDate || ''}
                      onChange={handleChange}
                      placeholder="YYYY-MM-DD"
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    前職あり
                    <input
                      type="text"
                      name="prePosition"
                      value={formData.prePosition || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                </div>

                {/* Section 3: 連絡先 */}
                <div className="emp-form-section">
                  <h4 className="emp-form-section-title">連絡先・住所</h4>
                  <label className="emp-form-label">
                    郵便番号
                    <input
                      type="text"
                      name="zipcode"
                      value={formData.zipcode || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    住所
                    <input
                      type="text"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    電話番号
                    <input
                      type="text"
                      name="tel"
                      value={formData.tel || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    メールアドレス
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                </div>

                {/* Section 4: 給与 */}
                <div className="emp-form-section">
                  <h4 className="emp-form-section-title">給与・有給</h4>
                  <label className="emp-form-label">
                    通勤費（円）
                    <input
                      type="number"
                      name="trainFare"
                      value={formData.trainFare ?? 0}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    扶養人数
                    <input
                      type="number"
                      name="fuyoucount"
                      value={formData.fuyoucount ?? 0}
                      onChange={handleChange}
                      className="emp-form-input"
                    />
                  </label>
                  <label className="emp-form-label">
                    発行日
                    <input
                      type="text"
                      name="issueDate"
                      value={formData.issueDate || ''}
                      onChange={handleChange}
                      placeholder="YYYY-MM-DD"
                      className="emp-form-input"
                    />
                  </label>
                  {modalMode === 'edit' && (
                    <div style={{ marginTop: 8 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>写真</span>
                      {formData.photo_url && (
                        <div className="emp-photo-preview-sm" style={{ marginTop: 8 }}>
                          <img src={formData.photo_url} alt="現在の写真" />
                        </div>
                      )}
                      <input
                        ref={editPhotoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file || !formData.id) return;
                          await handlePhotoUpload(file, formData.id, (url) => {
                            setFormData(prev => ({ ...prev, photo_url: url }));
                          });
                          e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        className="btn-emp-photo"
                        style={{ marginTop: 8, display: 'block', width: '100%' }}
                        onClick={() => editPhotoInputRef.current?.click()}
                        disabled={!formData.id}
                      >
                        {photoUploading ? 'アップロード中...' : '写真をアップロード'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="emp-form-footer">
                <button type="button" className="btn-emp-cancel" onClick={closeModal}>
                  キャンセル
                </button>
                <button type="submit" className="btn-emp-save" disabled={saving}>
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 写真管理モーダル */}
      {photoModal.open && (
        <div className="emp-modal-overlay" onClick={closePhotoModal}>
          <div className="emp-modal emp-photo-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="emp-modal-title">
              写真管理 — {photoModal.employee?.name || photoModal.employee?.employeeId}
            </h3>

            {photoModal.employee?.photo_url && (
              <div className="emp-photo-preview">
                <img src={photoModal.employee.photo_url} alt="現在の写真" />
              </div>
            )}

            <div
              className={`emp-photo-dropzone${photoDragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setPhotoDragOver(true); }}
              onDragLeave={() => setPhotoDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setPhotoDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file) {
                  handlePhotoUpload(file, photoModal.employee.id, (url) => {
                    setPhotoModal(prev => ({
                      ...prev,
                      employee: { ...prev.employee, photo_url: url },
                    }));
                  });
                }
              }}
              onClick={() => photoInputRef.current?.click()}
            >
              {photoUploading
                ? 'アップロード中...'
                : 'ここに画像をドラッグ＆ドロップ、またはクリックして選択'}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handlePhotoUpload(file, photoModal.employee.id, (url) => {
                      setPhotoModal(prev => ({
                        ...prev,
                        employee: { ...prev.employee, photo_url: url },
                      }));
                    });
                  }
                  e.target.value = '';
                }}
              />
            </div>

            <div className="emp-form-footer">
              {photoModal.employee?.photo_url && (
                <button type="button" className="btn-emp-delete-confirm" onClick={handlePhotoDelete}>
                  削除
                </button>
              )}
              <button type="button" className="btn-emp-cancel" onClick={closePhotoModal}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 削除確認ダイアログ */}
      {deleteTarget && (
        <div className="emp-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="emp-modal emp-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="emp-modal-title">削除の確認</h3>
            <p className="emp-delete-msg">
              <strong>{deleteTarget.name || deleteTarget.employeeId}</strong>（{deleteTarget.employeeId}）を削除しますか？
              <br />
              <small>※ 物理削除ではなく、削除フラグが設定されます。</small>
            </p>
            <div className="emp-form-footer">
              <button type="button" className="btn-emp-cancel" onClick={() => setDeleteTarget(null)}>
                キャンセル
              </button>
              <button type="button" className="btn-emp-delete-confirm" onClick={handleDelete}>
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}